import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Sparkles, Building2, Info, Rows3, Download } from "lucide-react";
import { DateFilter } from "@/components/ui/DateFilter";
import { Button } from "@/components/ui/enhanced-button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DateFilterKey, resolveDateFilter } from "@/utils/dateFilters";
import { PLLine, PLSection, ProfitAndLoss } from "@/interfaces/profitAndLoss.interface";
import { fetchProfitAndLoss } from "@/services/profitLoss.service";
import { buildProfitAndLossCsvRows, EMPHASIZED_SECTIONS, formatCurrency, SECTION_ORDER, TOTAL_ONLY_SECTIONS } from "./utils";
import { downloadCsv } from "@/utils/csv";
import LineItemDrilldownSheet, { DrilldownLine } from "./LineItemDrilldownSheet";
import { ManageVirtualEntriesModal } from "@/components/ManageVirtualEntriesModal";
import { canManageVirtualEntries } from "@/lib/permissions";
import { UserOrganizationRole, PLSectionKey } from "@blinkbooks/types";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useDensity } from "@/contexts/DensityContext";
import { listIntercompanyTags, createIntercompanyTag, deleteIntercompanyTag } from "@/services/intercompanyTag.service";
import { cn } from "@/lib/utils";

interface ProfitAndLossStatementProps {
  dateFilterKey: DateFilterKey | "";
  dateCustomFrom?: Date;
  dateCustomTo?: Date;
  onDateFilterChange: (key: DateFilterKey | "", from?: Date, to?: Date) => void;
  role?: UserOrganizationRole | null;
}

const ProfitAndLossStatement = ({
  dateFilterKey,
  dateCustomFrom,
  dateCustomTo,
  onDateFilterChange,
  role,
}: ProfitAndLossStatementProps) => {
  const [pl, setPl] = useState<ProfitAndLoss | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());
  const [selectedLine, setSelectedLine] = useState<DrilldownLine | null>(null);
  const [manageVirtualEntriesOpen, setManageVirtualEntriesOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [accountTagIdByKey, setAccountTagIdByKey] = useState<Map<string, string>>(new Map());
  const { activeViewId, activeOrganizationId } = useOrganization();
  const { density, toggleDensity } = useDensity();
  const linePadY = density === "compact" ? "py-0.5" : "py-1.5";
  const sectionPadY = density === "compact" ? "py-1" : "py-2";
  const canTagAccounts = !activeViewId && !!activeOrganizationId && canManageVirtualEntries(role);

  useEffect(() => {
    if (!canTagAccounts) {
      setAccountTagIdByKey(new Map());
      return;
    }
    listIntercompanyTags()
      .then((tags) => {
        const map = new Map<string, string>();
        tags
          .filter((t) => t.entityType === "pl_account" && t.sectionKey)
          .forEach((t) => map.set(`${t.sectionKey}::${t.entityId.trim().toLowerCase()}`, t.id));
        setAccountTagIdByKey(map);
      })
      .catch(() => {
        // Best-effort — the tag toggle just starts untagged if this fails to load.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canTagAccounts, activeOrganizationId]);

  const toggleAccountTag = async (sectionKey: PLSectionKey, lineName: string) => {
    const mapKey = `${sectionKey}::${lineName.trim().toLowerCase()}`;
    const existingTagId = accountTagIdByKey.get(mapKey);
    try {
      if (existingTagId) {
        await deleteIntercompanyTag(existingTagId);
        setAccountTagIdByKey((prev) => {
          const next = new Map(prev);
          next.delete(mapKey);
          return next;
        });
      } else {
        const tag = await createIntercompanyTag({ entityType: "pl_account", entityId: lineName, sectionKey });
        setAccountTagIdByKey((prev) => new Map(prev).set(mapKey, tag.id));
      }
    } catch {
      // Best-effort — a transient failure leaves the tag state unchanged; the user can retry.
    }
  };

  const range = resolveDateFilter(dateFilterKey || "this-year", dateCustomFrom, dateCustomTo) ?? {
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
  };

  // QBO's own multi-month reports append a literal "Total" column at the end of `pl.columns`.
  // We already render a dedicated sticky Total column, so drop that trailing duplicate here
  // (it's always the last entry when present, so slicing it off keeps amounts[i] indexing intact).
  const rawColumns = pl?.columns ?? [];
  const displayColumns =
    rawColumns.length > 0 && rawColumns[rawColumns.length - 1]!.trim().toLowerCase() === "total"
      ? rawColumns.slice(0, -1)
      : rawColumns;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProfitAndLoss(range)
      .then((data) => {
        if (!cancelled) setPl(data);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load Profit & Loss statement.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.startDate, range.endDate, refreshKey]);

  const handleExportCsv = () => {
    if (!pl) return;
    const rows = buildProfitAndLossCsvRows(pl, displayColumns);
    downloadCsv(`profit-and-loss_${range.startDate}_to_${range.endDate}.csv`, rows);
  };

  const toggleExpand = (key: string) => {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderLine = (line: PLLine, path: string, depth: number): JSX.Element[] => {
    const key = `${path}/${line.name}`;
    const hasChildren = (line.children?.length ?? 0) > 0;
    const isExpanded = expandedLines.has(key);
    const columnCount = displayColumns.length;
    const lineTotal = line.amounts.length > 0 ? line.amounts[line.amounts.length - 1]! : 0;

    const row = (
      <tr key={key} className="border-b border-border/20 last:border-0 hover:bg-muted/10 transition-colors">
        <td
          className={cn(linePadY, "sticky left-0 z-10 bg-card pr-3 w-64 min-w-[16rem] max-w-[16rem]")}
          style={{ paddingLeft: `${depth * 1.25 + 0.5}rem` }}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {hasChildren ? (
              <button
                className="flex items-center justify-center w-4 h-4 text-muted-foreground/50 hover:text-muted-foreground shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(key);
                }}
              >
                {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            ) : (
              <span className="w-4 shrink-0" />
            )}
            <button
              className="text-sm text-foreground/90 hover:text-primary hover:underline text-left truncate"
              onClick={() => setSelectedLine({ name: line.name, hasChildren })}
              title="View sub-accounts, vendors, and transactions"
            >
              {line.name}
            </button>
            {line.hasVirtualAdjustment && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Sparkles className="h-3 w-3 text-amber-500 shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  Includes a virtual forecast adjustment
                </TooltipContent>
              </Tooltip>
            )}
            {depth === 0 && canTagAccounts && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleAccountTag(path as PLSectionKey, line.name);
                }}
                title={
                  accountTagIdByKey.has(`${path}::${line.name.trim().toLowerCase()}`)
                    ? "Remove intercompany tag (this account will be included in consolidated totals again)"
                    : "Tag as intercompany (excluded from consolidated totals, kept in this single-company view)"
                }
                className={cn(
                  "inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0 transition-colors",
                  accountTagIdByKey.has(`${path}::${line.name.trim().toLowerCase()}`)
                    ? "text-violet-600 dark:text-violet-400"
                    : "text-muted-foreground/25 hover:text-muted-foreground"
                )}
              >
                <Building2 className="h-3 w-3" />
              </button>
            )}
          </div>
        </td>
        <td className={cn(linePadY, "sticky left-64 z-10 bg-card px-3 w-32 min-w-[8rem] text-sm tabular-nums text-foreground/90 text-right whitespace-nowrap border-r border-border/20")}>
          {formatCurrency(lineTotal)}
        </td>
        {Array.from({ length: columnCount }, (_, i) => (
          <td key={i} className={cn(linePadY, "px-3 text-sm tabular-nums text-foreground/90 text-right whitespace-nowrap")}>
            {formatCurrency(line.amounts[i] ?? 0)}
          </td>
        ))}
      </tr>
    );

    if (!hasChildren || !isExpanded) return [row];
    return [row, ...line.children!.flatMap((child) => renderLine(child, key, depth + 1))];
  };

  const renderSectionRows = (section: PLSection): JSX.Element[] => {
    const isTotalOnly = TOTAL_ONLY_SECTIONS.has(section.key);
    const emphasized = EMPHASIZED_SECTIONS.has(section.key);
    const columnCount = displayColumns.length;
    const rows: JSX.Element[] = [];

    if (isTotalOnly) {
      rows.push(
        <tr
          key={section.key}
          className={emphasized ? "font-bold text-foreground border-t border-b border-border/40" : "font-semibold text-foreground/90"}
        >
          <td className={cn(sectionPadY, "sticky left-0 z-10 bg-background px-2 w-64 min-w-[16rem] max-w-[16rem]")}>{section.label}</td>
          <td className={cn(sectionPadY, "sticky left-64 z-10 bg-background px-3 w-32 min-w-[8rem] text-sm tabular-nums text-right whitespace-nowrap border-r border-border/20")}>
            {formatCurrency(section.total)}
          </td>
          {columnCount > 0 && <td colSpan={columnCount} />}
        </tr>,
      );
      return rows;
    }

    rows.push(
      <tr key={`${section.key}-label`}>
        <td colSpan={columnCount + 2} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 pb-1 pt-3">
          {section.label}
        </td>
      </tr>,
    );

    if (section.lines.length === 0) {
      rows.push(
        <tr key={`${section.key}-empty`}>
          <td colSpan={columnCount + 2} className="py-3 px-3 text-xs text-muted-foreground/60 italic">
            No line items
          </td>
        </tr>,
      );
    } else {
      rows.push(...section.lines.flatMap((line) => renderLine(line, section.key, 0)));
    }

    rows.push(
      <tr
        key={`${section.key}-total`}
        className={emphasized ? "font-bold text-foreground border-t border-b border-border/40 mt-1" : "font-semibold text-foreground/90"}
      >
        <td className={cn(sectionPadY, "sticky left-0 z-10 bg-background px-2 w-64 min-w-[16rem] max-w-[16rem]")}>Total for {section.label}</td>
        <td className={cn(sectionPadY, "sticky left-64 z-10 bg-background px-3 w-32 min-w-[8rem] text-sm tabular-nums text-right whitespace-nowrap border-r border-border/20")}>
          {formatCurrency(section.total)}
        </td>
        {columnCount > 0 && <td colSpan={columnCount} />}
      </tr>,
    );

    return rows;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <h3 className="text-base font-semibold text-foreground">Profit & Loss</h3>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="p-0.5 rounded-full text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50 transition-colors"
                  aria-label="How intercompany eliminations work"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="start" sideOffset={8} className="w-80 text-sm leading-relaxed">
                <p className="font-semibold mb-1.5 text-foreground">Intercompany eliminations</p>
                <p className="text-muted-foreground">
                  Click the <Building2 className="inline h-3 w-3 -mt-0.5" /> icon next to a top-level account
                  (visible when viewing one company, not a consolidated View) to tag it as intercompany —
                  e.g. a management fee that's revenue in one entity and an expense in a related one.
                </p>
                <p className="text-muted-foreground mt-2">
                  Tagged accounts are excluded from consolidated totals whenever a View combines two or more
                  companies, so the combined P&L isn't inflated by transactions between related entities. Each
                  company's own single-entity P&L is unaffected — the account still shows there.
                </p>
                <p className="text-muted-foreground mt-2">
                  Only top-level accounts can be tagged (not sub-accounts nested under them).
                </p>
              </PopoverContent>
            </Popover>
          </div>
          {pl && pl.entities.length > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Consolidated across {pl.entities.length} {pl.entities.length === 1 ? "entity" : "entities"}:{" "}
              {pl.entities.map((e) => e.name).join(", ")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canManageVirtualEntries(role) && (
            <Button variant="outline" size="sm" onClick={() => setManageVirtualEntriesOpen(true)} className="gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Manage virtual entries
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={!pl}
            title="Export to CSV"
            className="w-9 px-0"
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleDensity}
            aria-pressed={density === "compact"}
            title={density === "compact" ? "Switch to comfortable row spacing" : "Switch to compact row spacing"}
            className={cn("w-9 px-0", density === "compact" && "bg-accent text-accent-foreground")}
          >
            <Rows3 className="h-3.5 w-3.5" />
          </Button>
          <DateFilter value={dateFilterKey} customFrom={dateCustomFrom} customTo={dateCustomTo} onChange={onDateFilterChange} />
        </div>
      </div>

      {loading && (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading Profit & Loss...</span>
        </div>
      )}

      {!loading && error && <p className="text-sm text-destructive py-8 text-center">{error}</p>}

      {!loading && !error && pl && (
        <div className="overflow-x-auto rounded-lg border border-border/30">
          <table className="min-w-max w-full border-collapse">
            <thead className="sticky top-0 z-20 bg-background">
              <tr className="border-b border-border/40">
                <th className={cn(sectionPadY, "sticky left-0 z-30 bg-background text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 w-64 min-w-[16rem] max-w-[16rem]")}>
                  Line Item
                </th>
                <th className={cn(sectionPadY, "sticky left-64 z-30 bg-background text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 w-32 min-w-[8rem] whitespace-nowrap border-r border-border/20")}>
                  Total
                </th>
                {displayColumns.map((col) => (
                  <th
                    key={col}
                    className={cn(sectionPadY, "text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 whitespace-nowrap")}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SECTION_ORDER.map((key) => {
                const section = pl.sections.find((s) => s.key === key);
                return section ? renderSectionRows(section) : null;
              })}
            </tbody>
          </table>
        </div>
      )}

      <LineItemDrilldownSheet
        line={selectedLine}
        columns={pl?.columns ?? []}
        range={range}
        onClose={() => setSelectedLine(null)}
      />

      <ManageVirtualEntriesModal
        open={manageVirtualEntriesOpen}
        onClose={() => setManageVirtualEntriesOpen(false)}
        onChanged={() => setRefreshKey((k) => k + 1)}
        activeViewId={activeViewId}
      />
    </div>
  );
};

export default ProfitAndLossStatement;
